/**
 * Utilities for importing catalog code mappings (colors and sizes) from CSV format.
 */

import Papa from 'papaparse';
import type { ColorProperty, SizeProperty, ColorPropertyInput, SizePropertyInput } from '@src/types';
import * as RewareApi from '@src/apis/reware';

export interface ParsedColorProperty {
  data: ColorPropertyInput;
  rowIndex: number;
  errors: string[];
}

export interface ParsedSizeProperty {
  data: SizePropertyInput;
  rowIndex: number;
  errors: string[];
}

export interface ImportStats {
  toAdd: number;
  toUpdate: number;
  unchanged: number;
  totalErrors: number;
  errors: string[];
}

export interface ImportResult {
  success: boolean;
  added: number;
  updated: number;
  failed: number;
  errors: string[];
}

/**
 * Splits a pipe-delimited string into an array.
 */
function pipeDelimitedToArray(value: string | undefined | null): string[] {
  if (!value || value.trim() === '') {
    return [];
  }
  return value.split('|').map(v => v.trim()).filter(v => v !== '');
}

/**
 * Validates a hex color code.
 */
function isValidHexCode(hex: string | undefined | null): boolean {
  if (!hex) return true; // Optional field
  return /^#[0-9A-Fa-f]{6}$/.test(hex);
}

/**
 * Validates a URL.
 */
function isValidUrl(url: string | undefined | null): boolean {
  if (!url) return true; // Optional field
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Validates a color property.
 */
export function validateColorProperty(property: ColorPropertyInput): string[] {
  const errors: string[] = [];

  if (!property.code || property.code.trim() === '') {
    errors.push('Missing required field: code');
  }

  if (!property.display || property.display.trim() === '') {
    errors.push('Missing required field: display');
  }

  if (!isValidHexCode(property.hex_code)) {
    errors.push(`Invalid hex code format: ${property.hex_code} (expected #RRGGBB)`);
  }

  if (!isValidUrl(property.url)) {
    errors.push(`Invalid URL format: ${property.url}`);
  }

  return errors;
}

/**
 * Validates a size property.
 */
export function validateSizeProperty(property: SizePropertyInput): string[] {
  const errors: string[] = [];

  if (!property.code || property.code.trim() === '') {
    errors.push('Missing required field: code');
  }

  if (!property.display || property.display.trim() === '') {
    errors.push('Missing required field: display');
  }

  return errors;
}

/**
 * Parses a CSV file containing color properties.
 * Expected format: code,display,hex_code,url,family,synonyms
 */
export function parseColorPropertiesCSV(content: string): ParsedColorProperty[] {
  const result = Papa.parse<Record<string, string>>(content, {
    header: true,
    skipEmptyLines: true,
    transformHeader: h => h.toLowerCase().trim(),
  });

  const parsed: ParsedColorProperty[] = [];

  result.data.forEach((row, index) => {
    const rowIndex = index + 2; // +2 because: 0-indexed + header row
    const data: ColorPropertyInput = {
      code: row.code?.trim() || '',
      display: row.display?.trim() || '',
      hex_code: row.hex_code?.trim() || undefined,
      url: row.url?.trim() || undefined,
      family: pipeDelimitedToArray(row.family),
      synonyms: pipeDelimitedToArray(row.synonyms),
    };

    const errors = validateColorProperty(data);

    parsed.push({
      data,
      rowIndex,
      errors,
    });
  });

  // Add papaparse errors
  result.errors.forEach((err) => {
    const rowNum = err.row !== undefined ? err.row + 2 : 0;
    if (rowNum > 0 && rowNum - 2 < parsed.length) {
      parsed[rowNum - 2].errors.push(err.message);
    }
  });

  markDuplicateCodesInFile(parsed);

  return parsed;
}

/**
 * Parses a CSV file containing size properties.
 * Expected format: code,display,family,primary_value,primary_label,secondary_value,secondary_label,tertiary_value,tertiary_label,synonyms
 */
export function parseSizePropertiesCSV(content: string): ParsedSizeProperty[] {
  const result = Papa.parse<Record<string, string>>(content, {
    header: true,
    skipEmptyLines: true,
    transformHeader: h => h.toLowerCase().trim(),
  });

  const parsed: ParsedSizeProperty[] = [];

  result.data.forEach((row, index) => {
    const rowIndex = index + 2; // +2 because: 0-indexed + header row
    const data: SizePropertyInput = {
      code: row.code?.trim() || '',
      display: row.display?.trim() || '',
      family: pipeDelimitedToArray(row.family),
      primary_value: row.primary_value?.trim() || undefined,
      primary_label: row.primary_label?.trim() || undefined,
      secondary_value: row.secondary_value?.trim() || undefined,
      secondary_label: row.secondary_label?.trim() || undefined,
      tertiary_value: row.tertiary_value?.trim() || undefined,
      tertiary_label: row.tertiary_label?.trim() || undefined,
      synonyms: pipeDelimitedToArray(row.synonyms),
    };

    const errors = validateSizeProperty(data);

    parsed.push({
      data,
      rowIndex,
      errors,
    });
  });

  // Add papaparse errors
  result.errors.forEach((err) => {
    const rowNum = err.row !== undefined ? err.row + 2 : 0;
    if (rowNum > 0 && rowNum - 2 < parsed.length) {
      parsed[rowNum - 2].errors.push(err.message);
    }
  });

  markDuplicateCodesInFile(parsed);

  return parsed;
}

/**
 * Checks if two arrays are equal (order-independent). Copies before sorting
 * so the caller's arrays (which may be live React state) aren't mutated.
 */
function arraysEqual(a: string[] | undefined, b: string[] | undefined): boolean {
  const arr1 = [...(a || [])].sort();
  const arr2 = [...(b || [])].sort();
  return arr1.length === arr2.length && arr1.every((val, idx) => val === arr2[idx]);
}

/**
 * Flags rows whose code collides with another row in the same import file.
 * Without this, two new rows sharing a code both look like "create" to
 * calculateImportStats/import*Properties and can be created concurrently.
 */
function markDuplicateCodesInFile<T extends { data: { code: string }; rowIndex: number; errors: string[] }>(
  parsed: T[]
): void {
  const rowsByCode = new Map<string, number[]>();

  parsed.forEach((item) => {
    const code = item.data.code?.trim();
    if (!code) return;
    const key = code.toLowerCase();
    const rows = rowsByCode.get(key) || [];
    rows.push(item.rowIndex);
    rowsByCode.set(key, rows);
  });

  parsed.forEach((item) => {
    const code = item.data.code?.trim();
    if (!code) return;
    const rows = rowsByCode.get(code.toLowerCase())!;
    if (rows.length > 1) {
      const otherRows = rows.filter((r) => r !== item.rowIndex);
      item.errors.push(`Duplicate code "${code}" also appears in row(s) ${otherRows.join(', ')}`);
    }
  });
}

/**
 * Checks if a color property is unchanged.
 */
function isColorPropertyUnchanged(parsed: ColorPropertyInput, existing: ColorProperty): boolean {
  return (
    parsed.code === existing.code &&
    parsed.display === existing.display &&
    (parsed.hex_code || null) === (existing.hex_code || null) &&
    (parsed.url || null) === (existing.url || null) &&
    arraysEqual(parsed.family, existing.family) &&
    arraysEqual(parsed.synonyms, existing.synonyms)
  );
}

/**
 * Checks if a size property is unchanged.
 */
function isSizePropertyUnchanged(parsed: SizePropertyInput, existing: SizeProperty): boolean {
  return (
    parsed.code === existing.code &&
    parsed.display === existing.display &&
    arraysEqual(parsed.family, existing.family) &&
    (parsed.primary_value || null) === (existing.primary_value || null) &&
    (parsed.primary_label || null) === (existing.primary_label || null) &&
    (parsed.secondary_value || null) === (existing.secondary_value || null) &&
    (parsed.secondary_label || null) === (existing.secondary_label || null) &&
    (parsed.tertiary_value || null) === (existing.tertiary_value || null) &&
    (parsed.tertiary_label || null) === (existing.tertiary_label || null) &&
    arraysEqual(parsed.synonyms, existing.synonyms)
  );
}

/**
 * Calculates import statistics for color properties.
 */
export function calculateColorImportStats(
  parsed: ParsedColorProperty[],
  existing: ColorProperty[]
): ImportStats {
  const existingMap = new Map(existing.map(p => [p.code, p]));

  let toAdd = 0;
  let toUpdate = 0;
  let unchanged = 0;
  const errors: string[] = [];

  parsed.forEach(item => {
    if (item.errors.length > 0) {
      errors.push(`Row ${item.rowIndex}: ${item.errors.join('; ')}`);
      return;
    }

    const existingProperty = existingMap.get(item.data.code);
    if (!existingProperty) {
      toAdd++;
    } else if (isColorPropertyUnchanged(item.data, existingProperty)) {
      unchanged++;
    } else {
      toUpdate++;
    }
  });

  return {
    toAdd,
    toUpdate,
    unchanged,
    totalErrors: errors.length,
    errors,
  };
}

/**
 * Calculates import statistics for size properties.
 */
export function calculateSizeImportStats(
  parsed: ParsedSizeProperty[],
  existing: SizeProperty[]
): ImportStats {
  const existingMap = new Map(existing.map(p => [p.code, p]));

  let toAdd = 0;
  let toUpdate = 0;
  let unchanged = 0;
  const errors: string[] = [];

  parsed.forEach(item => {
    if (item.errors.length > 0) {
      errors.push(`Row ${item.rowIndex}: ${item.errors.join('; ')}`);
      return;
    }

    const existingProperty = existingMap.get(item.data.code);
    if (!existingProperty) {
      toAdd++;
    } else if (isSizePropertyUnchanged(item.data, existingProperty)) {
      unchanged++;
    } else {
      toUpdate++;
    }
  });

  return {
    toAdd,
    toUpdate,
    unchanged,
    totalErrors: errors.length,
    errors,
  };
}

/**
 * Imports color properties with batch processing.
 */
export async function importColorProperties(
  partnerCode: string,
  token: string,
  parsed: ParsedColorProperty[],
  existing: ColorProperty[],
  onProgress?: (percent: number) => void
): Promise<ImportResult> {
  const existingMap = new Map(existing.map(p => [p.code, p]));
  const validItems = parsed.filter(item => item.errors.length === 0);

  let added = 0;
  let updated = 0;
  let failed = 0;
  const errors: string[] = [];

  const BATCH_SIZE = 10;

  for (let i = 0; i < validItems.length; i += BATCH_SIZE) {
    const batch = validItems.slice(i, i + BATCH_SIZE);

    const promises = batch.map(async (item) => {
      try {
        const existingProperty = existingMap.get(item.data.code);

        if (!existingProperty) {
          // Create new
          await RewareApi.createColorProperty({ partnerCode, token, data: item.data });
          return { action: 'added', rowIndex: item.rowIndex };
        } else if (!isColorPropertyUnchanged(item.data, existingProperty)) {
          // Update existing
          await RewareApi.updateColorProperty({
            id: existingProperty.id,
            partnerCode,
            token,
            data: item.data,
          });
          return { action: 'updated', rowIndex: item.rowIndex };
        } else {
          // Unchanged
          return { action: 'unchanged', rowIndex: item.rowIndex };
        }
      } catch (error) {
        return {
          action: 'failed',
          rowIndex: item.rowIndex,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    });

    const results = await Promise.all(promises);

    results.forEach(result => {
      if (result.action === 'added') {
        added++;
      } else if (result.action === 'updated') {
        updated++;
      } else if (result.action === 'failed') {
        failed++;
        errors.push(`Row ${result.rowIndex}: ${result.error}`);
      }
    });

    // Update progress
    const progress = Math.round(((i + batch.length) / validItems.length) * 100);
    onProgress?.(progress);
  }

  return {
    success: failed === 0,
    added,
    updated,
    failed,
    errors,
  };
}

/**
 * Imports size properties with batch processing.
 */
export async function importSizeProperties(
  partnerCode: string,
  token: string,
  parsed: ParsedSizeProperty[],
  existing: SizeProperty[],
  onProgress?: (percent: number) => void
): Promise<ImportResult> {
  const existingMap = new Map(existing.map(p => [p.code, p]));
  const validItems = parsed.filter(item => item.errors.length === 0);

  let added = 0;
  let updated = 0;
  let failed = 0;
  const errors: string[] = [];

  const BATCH_SIZE = 10;

  for (let i = 0; i < validItems.length; i += BATCH_SIZE) {
    const batch = validItems.slice(i, i + BATCH_SIZE);

    const promises = batch.map(async (item) => {
      try {
        const existingProperty = existingMap.get(item.data.code);

        if (!existingProperty) {
          // Create new
          await RewareApi.createSizeProperty({ partnerCode, token, data: item.data });
          return { action: 'added', rowIndex: item.rowIndex };
        } else if (!isSizePropertyUnchanged(item.data, existingProperty)) {
          // Update existing
          await RewareApi.updateSizeProperty({
            id: existingProperty.id,
            partnerCode,
            token,
            data: item.data,
          });
          return { action: 'updated', rowIndex: item.rowIndex };
        } else {
          // Unchanged
          return { action: 'unchanged', rowIndex: item.rowIndex };
        }
      } catch (error) {
        return {
          action: 'failed',
          rowIndex: item.rowIndex,
          error: error instanceof Error ? error.message : 'Unknown error',
        };
      }
    });

    const results = await Promise.all(promises);

    results.forEach(result => {
      if (result.action === 'added') {
        added++;
      } else if (result.action === 'updated') {
        updated++;
      } else if (result.action === 'failed') {
        failed++;
        errors.push(`Row ${result.rowIndex}: ${result.error}`);
      }
    });

    // Update progress
    const progress = Math.round(((i + batch.length) / validItems.length) * 100);
    onProgress?.(progress);
  }

  return {
    success: failed === 0,
    added,
    updated,
    failed,
    errors,
  };
}
